/**
 * Flight hours and days still needed to finish a project, from the project's own record (MB's R100, 26 Oct 2026;
 * docs/specs/project-reporting/overview-estimates.md). Every input is an existing rule, so Teslyn Field's Project
 * overview and Teslyn Cloud's Operations Hub give the same figures from the same rows:
 *
 * - lines flown but not yet reviewed count as done (decision 716): `asReviewed` treats a pending line as accepted at
 *   its proposed length, as accepting it in Line review does, for the remaining km and for the rate.
 * - remaining km: `planProgress(...).remainingM` (decision 29, the lines-remaining map and the Job Summary's
 *   "Est KM Remaining") on those statuses (`estimateRemainingKm`): every original line not accepted in full at its
 *   planned length, a line with a patch still to fly at the patch's length.
 * - production rate: the Operations Hub's accepted km per survey hour (`opsSummary`, TJR "Acptd Line KM/Hr"):
 *   accepted km ÷ the survey hours of the flight entries. Survey hours run from the first survey line's start to the
 *   last one's end, turns included (decision 542).
 * - ferry and cal share: the comp box, cals and ferry hours flown on the project's flying days (days with survey
 *   hours) as a share of their survey hours. Days with no survey at all (the mobilisation ferry, a comp-box-only day)
 *   are one-offs and are left out of the share.
 * - flight hours per day: two figures, giving the range of days:
 *   - per flying day: the flight hours of the flying days ÷ the flying days (every remaining day flies);
 *   - per day on the job: the Operations Hub's "Average flight hours per day on the job" (MB's R52,
 *     `aircraftProjectRows` and `utilisationTotal`, the calendar days an aircraft is on the job, standby, maintenance
 *     and weather days included: the pace so far), with the days flown with neither survey nor comp box (the
 *     mobilisation ferry) left out (decision 715 as MB clarified it: a comp-box-only day stays a day on the job, its
 *     hours, like a standby day's, not in the rate). With several aircraft the hours are the project's
 *     combined hours per calendar day (decision 717).
 *
 * With fewer than `ESTIMATE_MIN_FLYING_DAYS` flying days, or no accepted km yet, there is no estimate and the reason
 * says why.
 */
import { hoursFlown } from './aircraft.js';
import { withCompletedBlocks } from './block-report.js';
import type { Block, IsoDate, LineStatus, PlannedLine, Uuid } from './model.js';
import {
  aircraftProjectRows,
  opsSummary,
  utilisationTotal,
  type OpsEntry,
  type OpsFilter,
  type OpsLine,
} from './ops.js';
import { planProgress } from './progress.js';
import { acceptedKmPerHour } from './tjr-summary.js';

/** Flying days needed before the project's own rates are trusted (MB's R100: "fewer than 3 flying days"). */
export const ESTIMATE_MIN_FLYING_DAYS = 3;

export interface EstimateBasis {
  /** First and last daily entry of the project; null with none. */
  readonly span: { readonly from: IsoDate; readonly to: IsoDate } | null;
  readonly remainingKm: number;
  /** Accepted km, lines flown but not yet reviewed included at their proposed length (decision 716). */
  readonly acceptedKm: number;
  /** The part of `acceptedKm` from lines still awaiting review. */
  readonly awaitingReviewKm: number;
  /** Survey hours of the flight entries (not mobilisation). */
  readonly surveyHours: number;
  /** Days with survey hours on a flight entry that is not mobilisation. */
  readonly flyingDays: number;
}

export interface EstimateReady extends EstimateBasis {
  readonly ready: true;
  /** Accepted km ÷ survey hours. */
  readonly kmPerSurveyHour: number;
  /** Flight hours (survey + comp box + cals + ferry) flown on the flying days. */
  readonly flyingDayHours: number;
  /** Comp box, cals and ferry hours on the flying days. */
  readonly overheadHours: number;
  /** `overheadHours` ÷ the survey hours: hours of ferry and cals per survey hour. */
  readonly overheadShare: number;
  /** Remaining km ÷ km per survey hour. */
  readonly surveyHoursRemaining: number;
  /** `surveyHoursRemaining` × (1 + `overheadShare`). */
  readonly flightHoursRemaining: number;
  /** `flyingDayHours` ÷ `flyingDays`. */
  readonly hoursPerFlyingDay: number;
  /** Calendar days an aircraft was on the job (R52 project days), before the ferry-only days are left out. */
  readonly projectDays: number;
  /**
   * Days flown with neither survey nor comp box (the mobilisation ferry), left out of the rate (decision 715). A
   * comp-box-only day is not one of them: it stays a day on the job.
   */
  readonly noSurveyDays: number;
  /** `projectDays` − `noSurveyDays`: the flying and comp-box days and the standby, maintenance, weather and idle days. */
  readonly daysOnJob: number;
  /** `flyingDayHours` ÷ `daysOnJob`: R52 without the days flown with neither survey nor comp box. */
  readonly hoursPerDayOnJob: number;
  /** Days remaining at each rate before rounding up, fewest first. */
  readonly daysExact: readonly [number, number];
  /** Whole days remaining (rounded up), fewest first; equal when both rates give the same whole number. */
  readonly days: readonly [number, number];
}

export interface EstimateNotReady extends EstimateBasis {
  readonly ready: false;
  /** Why there is no estimate, in words. */
  readonly reason: string;
}

export type ProjectEstimate = EstimateReady | EstimateNotReady;

/** A line flown but not yet reviewed as done (decision 716): accepted at its proposed length, as Line review does. */
export function asReviewed<S extends Pick<LineStatus, 'acceptance' | 'accepted_m' | 'proposed_m'>>(s: S): S {
  return s.acceptance === 'pending' ? { ...s, acceptance: 'accepted', accepted_m: s.proposed_m } : s;
}

/** Km still to fly for the estimate: `planProgress` with the lines awaiting review counted as done (decision 716). */
export function estimateRemainingKm(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Pick<Block, 'id' | 'km_completed'>[] = [],
): number {
  // N32: a block marked "km completed" has nothing left to fly
  return withCompletedBlocks(planProgress(statuses.map(asReviewed), plan), blocks).remainingM / 1000;
}

/**
 * The km of `estimateRemainingKm` by block (MB's R118, 27 Oct 2026: the Project overview's "Remaining km" column):
 * each original planned line's remaining length under its block's id, so the blocks add up to the project's figure.
 * A block with lines and nothing left to fly holds 0; a block with no planned lines is not listed.
 */
export function estimateRemainingKmByBlock(
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  blocks: readonly Pick<Block, 'id' | 'km_completed'>[] = [],
): ReadonlyMap<Uuid, number> {
  const out = new Map<Uuid, number>();
  for (const l of withCompletedBlocks(planProgress(statuses.map(asReviewed), plan), blocks).lines)
    out.set(l.original.block_id, (out.get(l.original.block_id) ?? 0) + l.remaining_m);
  for (const [block, m] of out) out.set(block, m / 1000);
  return out;
}

/**
 * The estimate for one project from its line statuses (as `opsLines` gives them), its daily entries and its
 * remaining km (`estimateRemainingKm`). Lines awaiting review count as done. Deleted entries must be left out by the
 * caller (the Operations Hub's rows never hold them).
 */
export function projectEstimate(
  projectId: Uuid,
  opsRows: readonly OpsLine[],
  entries: readonly OpsEntry[],
  remainingKm: number,
): ProjectEstimate {
  const pendingM = opsRows
    .filter((l) => l.project_id === projectId && l.acceptance === 'pending' && l.use_km)
    .reduce((a, l) => a + l.proposed_m, 0);
  const lines = opsRows.map(asReviewed);
  const mine = entries.filter((e) => e.project_id === projectId);
  const dates = mine.map((e) => e.date).sort();
  const span = dates.length > 0 ? { from: dates[0]!, to: dates[dates.length - 1]! } : null;
  const filter: OpsFilter | null = span ? { ...span, projectId, aircraftId: null } : null;
  const summary = filter ? opsSummary(lines, mine, filter) : null;
  const basis: EstimateBasis = {
    span,
    remainingKm: Math.max(0, remainingKm),
    acceptedKm: summary?.acceptedKm ?? 0,
    awaitingReviewKm: pendingM / 1000,
    surveyHours: summary?.surveyHours ?? 0,
    flyingDays: summary?.surveyDays ?? 0,
  };
  if (basis.flyingDays < ESTIMATE_MIN_FLYING_DAYS)
    return {
      ...basis,
      ready: false,
      reason:
        basis.flyingDays === 0
          ? `Needs ${ESTIMATE_MIN_FLYING_DAYS} flying days of survey; none yet.`
          : `Needs ${ESTIMATE_MIN_FLYING_DAYS} flying days of survey; ${basis.flyingDays} so far.`,
    };
  if (basis.acceptedKm <= 0 || basis.surveyHours <= 0)
    return { ...basis, ready: false, reason: 'No survey lines flown yet.' };

  // the flying days: dates with survey hours on a flight entry that is not mobilisation (the Job Summary's rule)
  const flight = mine.filter((e) => e.entry_type === 'flight' && !e.mobilization);
  const flyingDates = new Set(flight.filter((e) => e.svy_hours > 0).map((e) => e.date));
  const onFlyingDays = flight.filter((e) => flyingDates.has(e.date));
  const flyingDayHours = onFlyingDays.reduce((a, e) => a + hoursFlown(e), 0);
  const flyingDaySurvey = onFlyingDays.reduce((a, e) => a + e.svy_hours, 0);
  const overheadHours = Math.max(0, flyingDayHours - flyingDaySurvey);
  const overheadShare = flyingDaySurvey > 0 ? overheadHours / flyingDaySurvey : 0;

  const kmPerSurveyHour = acceptedKmPerHour(basis.acceptedKm, basis.surveyHours);
  const surveyHoursRemaining = basis.remainingKm / kmPerSurveyHour;
  const flightHoursRemaining = surveyHoursRemaining * (1 + overheadShare);
  const hoursPerFlyingDay = flyingDayHours / basis.flyingDays;

  // R52's days on the job, as the Operations Hub's aircraft table works them out for the project's whole span (the
  // calendar days any aircraft is on the job, decision 548), less the days flown with neither survey nor comp box
  // (decision 715, as clarified): their hours are already out of `flyingDayHours`. A comp-box-only day stays on the job
  const use = utilisationTotal(aircraftProjectRows(lines, mine, filter!));
  const span52 = use.projectSpan;
  const compBoxDates = new Set(mine.filter((e) => e.cbx_hours > 0).map((e) => e.date));
  const noSurveyDays = new Set(
    mine
      .filter((e) => hoursFlown(e) > 0 && !flyingDates.has(e.date) && !compBoxDates.has(e.date))
      .filter((e) => span52 !== null && e.date >= span52.from && e.date <= span52.to)
      .map((e) => e.date),
  ).size;
  const daysOnJob = Math.max(basis.flyingDays, use.projectDays - noSurveyDays);
  const hoursPerDayOnJob = flyingDayHours / daysOnJob;

  const atFlying = flightHoursRemaining / hoursPerFlyingDay;
  const atJob = flightHoursRemaining / hoursPerDayOnJob;
  const lo = Math.min(atFlying, atJob);
  const hi = Math.max(atFlying, atJob);
  return {
    ...basis,
    ready: true,
    kmPerSurveyHour,
    flyingDayHours,
    overheadHours,
    overheadShare,
    surveyHoursRemaining,
    flightHoursRemaining,
    hoursPerFlyingDay,
    projectDays: use.projectDays,
    noSurveyDays,
    daysOnJob,
    hoursPerDayOnJob,
    daysExact: [lo, hi],
    days: [Math.max(0, Math.ceil(lo - 1e-9)), Math.max(0, Math.ceil(hi - 1e-9))],
  };
}

const one = (n: number): string =>
  n.toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const two = (n: number): string =>
  n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The days figure as shown: "12" or "11–13". */
export function estimateDaysText(e: EstimateReady): string {
  return e.days[0] === e.days[1] ? String(e.days[0]) : `${e.days[0]}–${e.days[1]}`;
}

/** How the flight hours are worked out, line by line, for the tile's explanation. */
export function flightHoursExplained(e: ProjectEstimate): string[] {
  if (!e.ready) return [e.reason];
  return [
    `${two(e.remainingKm)} km still to fly (planned lines not yet flown or not accepted, reflights and patches included).`,
    `${two(e.kmPerSurveyHour)} accepted km per survey hour: ${two(e.acceptedKm)} km accepted in ${one(e.surveyHours)} survey hours over ${plural(e.flyingDays, 'flying day')}.`,
    ...(e.awaitingReviewKm > 0
      ? [`Lines flown and awaiting review count as done: ${two(e.awaitingReviewKm)} km of them.`]
      : []),
    `${two(e.remainingKm)} km ÷ ${two(e.kmPerSurveyHour)} = ${one(e.surveyHoursRemaining)} survey hours.`,
    `Plus ${one(100 * e.overheadShare)} % for comp box, cals and ferry (${one(e.overheadHours)} h of them on the flying days), giving ${one(e.flightHoursRemaining)} flight hours.`,
  ];
}

/** How the days are worked out, for the tile's explanation. */
export function daysExplained(e: ProjectEstimate): string[] {
  if (!e.ready) return [e.reason];
  const lines = [
    `${one(e.flightHoursRemaining)} flight hours ÷ ${one(e.hoursPerFlyingDay)} h per flying day (${one(e.flyingDayHours)} h over ${plural(e.flyingDays, 'flying day')}) = ${one(e.flightHoursRemaining / e.hoursPerFlyingDay)} days if every day flies.`,
  ];
  const leftOut =
    e.noSurveyDays > 0
      ? `; ${plural(e.noSurveyDays, 'day')} flown with no survey or comp box, such as the ferry to the job, left out`
      : '';
  lines.push(
    `${one(e.flightHoursRemaining)} flight hours ÷ ${one(e.hoursPerDayOnJob)} average flight hours per day on the job (${one(e.flyingDayHours)} h over ${plural(e.daysOnJob, 'day')} on the job, standby, maintenance and weather days included${leftOut}) = ${one(e.flightHoursRemaining / e.hoursPerDayOnJob)} days at the pace so far.`,
  );
  lines.push('Whole days, rounded up.');
  return lines;
}
